
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Editor, EditorContent } from '@tiptap/vue-2';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';

// By default Tiptap uses <strong> for bold, here we replace it by <b> to save space in the database
const ShortBold = Bold.extend({
	renderHTML({ HTMLAttributes }) {
		return ['b', HTMLAttributes, 0];
	}
});

// By default Tiptap uses <em> for italic, here we replace it by <i>
const ShortItalic = Italic.extend({
	renderHTML({ HTMLAttributes }) {
		return ['i', HTMLAttributes, 0];
	}
});

// By default Tiptap creates a new paragraphe (<p></p>) when we press enter, here we replace it by <br>
const EnterBreak = Extension.create({
	name: 'enterBreak',
	addKeyboardShortcuts() {
		return {
			Enter: () => this.editor.commands.setHardBreak()
		};
	}
});

@Component<RichTextEditor>({
	components: {
		EditorContent
	}
})
export default class RichTextEditor extends Vue {
	@Prop({ type: String, required: true }) private id!: string;
	@Prop({ type: String, default: '' }) private value!: string;
	@Prop({ type: String, default: '' }) private label!: string;
	@Prop({ type: String, default: '' }) private placeholder!: string;
	@Prop({ type: String, default: '' }) private errorMessage!: string;
	@Prop({ type: Boolean, default: false }) private disabled!: boolean;
	@Prop({ type: Number, default: 0 }) private counter!: number;
	@Watch('value')
	private onValueChange(newValue: string): void {
		if (this.editor && this.stripParagraphWrapper(this.editor.getHTML()) !== newValue) {
			this.editor.commands.setContent(newValue || '', { emitUpdate: false });
		}
	}
	@Watch('disabled')
	private onDisabledChange(newVal: boolean): void {
		this.editor?.setEditable(!newVal);
	}
	@Watch('placeholder')
	private onPlaceholderChange(newPlaceholder: string): void {
		if (this.editor) {
			this.editor.extensionManager.extensions
				.filter(ext => ext.name === 'placeholder')
				.forEach(ext => {
					ext.options.placeholder = newPlaceholder;
				});
			this.editor.view.dispatch(this.editor.state.tr);
		}
	}

	private editor: Editor | null = null;
	private activeFormats: string[] = [];
	private htmlLength: number = 0;

	private get characterCount(): string | undefined {
		if (!this.counter) {
			return undefined;
		}
		return `${this.htmlLength} / ${this.counter}`;
	}

	private get isOverLimit(): boolean {
		return this.counter > 0 && this.htmlLength > this.counter;
	}

	/**
	 * Create the TipTap editor instance
	 *
	 * @return {void}
	 */
	private mounted(): void {
		this.editor = new Editor({
			content: this.value || '',
			editable: !this.disabled,
			extensions: [
				StarterKit.configure({
					bold: false,
					italic: false,
					blockquote: false,
					bulletList: false,
					codeBlock: false,
					heading: false,
					horizontalRule: false,
					listItem: false,
					orderedList: false,
					code: false,
					strike: false,
					underline: false,
					link: false
				}),
				ShortBold,
				ShortItalic,
				Underline,
				EnterBreak,
				Placeholder.configure({
					placeholder: this.placeholder
				})
			],
			onUpdate: ({ editor }) => {
				const htmlString = this.stripParagraphWrapper(editor.getHTML());
				this.htmlLength = htmlString.length;
				this.$emit('input', htmlString);
			},
			onBlur: () => {
				this.$emit('blur');
			},
			onTransaction: ({ editor }) => {
				this.updateActiveStates(editor);
			}
		});

		this.htmlLength = this.stripParagraphWrapper(this.editor.getHTML()).length;
	}

	/**
	 * Destroy the TipTap editor instance
	 *
	 * @return {void}
	 */
	private destroyed(): void {
		if (this.editor && !this.editor.isDestroyed) {
			this.editor.destroy();
		}
		this.editor = null;
	}

	/**
	 * Update toolbar active states based on current editor selection
	 *
	 * @param {any} editor
	 * @return {void}
	 */
	private updateActiveStates(editor: any): void {
		const formats: string[] = [];

		if (editor.isActive('bold')) {
			formats.push('bold');
		}
		if (editor.isActive('italic')) {
			formats.push('italic');
		}
		if (editor.isActive('underline')) {
			formats.push('underline');
		}

		this.activeFormats = formats;
	}

	/**
	 * Toggle bold formatting
	 *
	 * @return {void}
	 */
	private toggleBold(): void {
		this.editor?.chain().focus().toggleBold().run();
	}

	/**
	 * Toggle italic formatting
	 *
	 * @return {void}
	 */
	private toggleItalic(): void {
		this.editor?.chain().focus().toggleItalic().run();
	}

	/**
	 * Toggle underline formatting
	 *
	 * @return {void}
	 */
	private toggleUnderline(): void {
		this.editor?.chain().focus().toggleUnderline().run();
	}

	/**
	 * Strip the outer <p></p> wrapper that TipTap adds to all content.
	 * This is done to prevent storing these wrapper tags in our database.
	 *
	 * @param {string} htmlString
	 * @return {string}
	 */
	private stripParagraphWrapper(htmlString: string): string {
		return htmlString.replace(/^<p>(.*)<\/p>$/s, '$1');
	}
}
